import { useState } from 'react';
import { Card, Button, Badge, Spinner, Label, Select } from 'flowbite-react';
import {
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { medicalApi } from '../services/api';
import toast from 'react-hot-toast';

export default function MedicalAnalytics() {
  const [loading, setLoading] = useState(false);
  const [cohortData, setCohortData] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [phiAuditLogs, setPhiAuditLogs] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    cancer_status: 'all',
    surgery_status: 'all',
    age_range: 'all',
    days: 90
  });

  async function loadCohortPatterns() {
    try {
      setLoading(true);
      const data = await medicalApi.cohortPatterns(filters);
      setCohortData(data);

      if (data.hipaa_compliance === 'rejected') {
        toast.error(data.detail || 'K-anonymity violation: cohort too small');
      } else {
        toast.success('Cohort patterns loaded successfully');
      }
    } catch (error) {
      console.error('Failed to load cohort patterns:', error);
      toast.error('Failed to load cohort patterns');
    } finally {
      setLoading(false);
    }
  }

  async function loadAnomalies() {
    try {
      setLoading(true);
      const data = await medicalApi.anomalyDetection(filters);
      setAnomalies(data);

      if (data.hipaa_compliance === 'rejected') {
        toast.error(data.detail || 'K-anonymity violation: cohort too small');
      } else {
        toast.success(`Found ${data.data?.length || 0} anomalies`);
      }
    } catch (error) {
      console.error('Failed to detect anomalies:', error);
      toast.error('Failed to detect anomalies');
    } finally {
      setLoading(false);
    }
  }

  async function loadPHIAuditLogs() {
    try {
      setLoading(true);
      const data = await medicalApi.phiAudit({ limit: 20 });
      setPhiAuditLogs(data);
      toast.success('PHI audit logs loaded');
    } catch (error) {
      console.error('Failed to load PHI audit logs:', error);
      toast.error('Failed to load PHI audit logs');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Medical Analytics</h1>
            <Badge color="success" icon={ShieldCheckIcon}>
              HIPAA Compliant
            </Badge>
          </div>
          <p className="mt-1 text-gray-600">
            Privacy-preserving urologic patient cohort analysis with k-anonymity ≥ 5
          </p>
        </div>
      </div>

      {/* HIPAA Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <ShieldCheckIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-blue-900">HIPAA Compliance Enabled</h3>
            <p className="text-sm text-blue-800 mt-1">
              All queries enforce k-anonymity (minimum 5 patients). PHI is encrypted at rest using
              AES-256-GCM. All access is logged to the PHI audit trail.
            </p>
          </div>
        </div>
      </Card>

      {/* Filters Card */}
      <Card>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Cohort Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <Label htmlFor="cancer_status">Cancer Status</Label>
            <Select
              id="cancer_status"
              value={filters.cancer_status}
              onChange={(e) => setFilters({ ...filters, cancer_status: e.target.value })}
            >
              <option value="all">All Patients</option>
              <option value="diagnosed">Diagnosed</option>
              <option value="suspected">Suspected</option>
              <option value="clear">Clear</option>
            </Select>
          </div>

          <div>
            <Label htmlFor="surgery_status">Surgery Status</Label>
            <Select
              id="surgery_status"
              value={filters.surgery_status}
              onChange={(e) => setFilters({ ...filters, surgery_status: e.target.value })}
            >
              <option value="all">All Patients</option>
              <option value="pre_op">Pre-operative</option>
              <option value="post_op">Post-operative</option>
              <option value="no_surgery">No Surgery</option>
            </Select>
          </div>

          <div>
            <Label htmlFor="age_range">Age Range</Label>
            <Select
              id="age_range"
              value={filters.age_range}
              onChange={(e) => setFilters({ ...filters, age_range: e.target.value })}
            >
              <option value="all">All Ages</option>
              <option value="18-40">18-40 years</option>
              <option value="41-60">41-60 years</option>
              <option value="61-80">61-80 years</option>
              <option value="81+">81+ years</option>
            </Select>
          </div>

          <div>
            <Label htmlFor="days">Time Range</Label>
            <Select
              id="days"
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
            >
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 180 days</option>
              <option value="365">Last year</option>
            </Select>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            color="blue"
            onClick={loadCohortPatterns}
            disabled={loading}
          >
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Analyze Cohort Patterns
          </Button>
          <Button
            color="purple"
            onClick={loadAnomalies}
            disabled={loading}
          >
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            Detect Anomalies
          </Button>
          <Button
            color="gray"
            onClick={loadPHIAuditLogs}
            disabled={loading}
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            View PHI Audit Log
          </Button>
        </div>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="xl" />
            <p className="mt-4 text-gray-600">Analyzing encrypted patient data...</p>
          </div>
        </Card>
      )}

      {/* Cohort Patterns Results */}
      {cohortData && !loading && (
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Cohort Voiding Patterns</h2>

          {cohortData.hipaa_compliance === 'rejected' ? (
            <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-900">K-Anonymity Violation</p>
                <p className="text-sm text-yellow-800">{cohortData.detail}</p>
                <p className="text-sm text-yellow-700 mt-1">{cohortData.suggestion}</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatCard
                  label="Cohort Size"
                  value={cohortData.data?.cohort_size || 0}
                  icon={<UserGroupIcon className="h-6 w-6" />}
                />
                <StatCard
                  label="Avg Daily Frequency"
                  value={(cohortData.data?.avg_daily_frequency || 0).toFixed(1)}
                  icon={<ChartBarIcon className="h-6 w-6" />}
                />
                <StatCard
                  label="Avg Volume (mL)"
                  value={(cohortData.data?.avg_volume_ml || 0).toFixed(0)}
                  icon={<ChartBarIcon className="h-6 w-6" />}
                />
              </div>

              <div className="text-sm text-gray-600">
                <p><strong>Data Range:</strong> {cohortData.data?.date_range_start} to {cohortData.data?.date_range_end}</p>
                <p className="mt-2"><strong>Privacy:</strong> K-anonymity ≥ 5 enforced ✓</p>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Anomaly Detection Results */}
      {anomalies && !loading && (
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Detected Anomalies</h2>

          {anomalies.hipaa_compliance === 'rejected' ? (
            <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-900">K-Anonymity Violation</p>
                <p className="text-sm text-yellow-800">{anomalies.detail}</p>
              </div>
            </div>
          ) : (
            <div>
              {anomalies.data?.length === 0 ? (
                <p className="text-gray-600">No statistical anomalies detected in this cohort.</p>
              ) : (
                <div className="space-y-3">
                  {anomalies.data?.map((anomaly, idx) => (
                    <div key={idx} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="font-semibold text-red-900">{anomaly.anomaly_type}</p>
                      <p className="text-sm text-red-800 mt-1">{anomaly.description}</p>
                      <p className="text-xs text-red-700 mt-2">
                        Deviation: {anomaly.std_dev_from_mean?.toFixed(2)}σ
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* PHI Audit Logs */}
      {phiAuditLogs && !loading && (
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4">PHI Access Audit Trail</h2>

          {phiAuditLogs.data?.length === 0 ? (
            <p className="text-gray-600">No PHI access logs recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Timestamp</th>
                    <th className="p-3 text-left">Admin User</th>
                    <th className="p-3 text-left">Access Type</th>
                    <th className="p-3 text-left">Query Params</th>
                    <th className="p-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {phiAuditLogs.data?.map((log) => (
                    <tr key={log.id} className="border-b">
                      <td className="p-3">{new Date(log.created_at).toLocaleString()}</td>
                      <td className="p-3">{log.admin_email}</td>
                      <td className="p-3">
                        <Badge color="blue">{log.access_type}</Badge>
                      </td>
                      <td className="p-3 text-xs text-gray-600">
                        {JSON.stringify(log.query_params)}
                      </td>
                      <td className="p-3">
                        <Badge color={log.k_anonymity_passed ? 'success' : 'failure'}>
                          {log.k_anonymity_passed ? 'Passed' : 'Rejected'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600">{label}</span>
        <div className="text-blue-600">{icon}</div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
